/** Automatic, privacy-safe interaction tracking mounted inside BrowserRouter. */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { flushTelemetry, trackTelemetry } from "@/services/telemetryService";
import {
  normalizeTelemetryPagePath,
  sanitizeTelemetryLabel,
  telemetryFieldName,
  telemetryValueLengthBucket,
  telemetryViewport,
} from "@/utils/telemetry";
import { startPerformanceTracking } from "@/utils/performanceTracker";
import { ANALYTICS_OPERATIONS, ANALYTICS_WORKFLOWS } from "@/constants/analyticsOperations";

const INTERACTIVE_SELECTOR = [
  "button",
  "a",
  "summary",
  "[role='button']",
  "[role='link']",
  "[role='menuitem']",
  "[role='tab']",
  "[role='checkbox']",
  "[role='radio']",
  "[data-telemetry-track]",
].join(",");

function isIgnored(element: Element): boolean {
  return Boolean(element.closest("[data-telemetry-ignore]"));
}

function semanticElementLabel(element: HTMLElement): string {
  const explicit = element.dataset.telemetryLabel
    ?? element.getAttribute("aria-label")
    ?? element.getAttribute("name")
    ?? element.getAttribute("title");
  if (explicit) return sanitizeTelemetryLabel(explicit);

  if (element instanceof HTMLAnchorElement) {
    return `link:${normalizeTelemetryPagePath(element.getAttribute("href") ?? "/")}`;
  }

  const text = element.textContent?.trim();
  if (text) return sanitizeTelemetryLabel(text);
  return element.tagName.toLocaleLowerCase("en");
}

function formName(form: HTMLFormElement | null): string {
  if (!form) return "form";
  return sanitizeTelemetryLabel(
    form.dataset.telemetryLabel
      || form.getAttribute("aria-label")
      || form.getAttribute("name")
      || form.id
      || "form",
  );
}

function isSensitiveControl(control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean {
  if (!(control instanceof HTMLInputElement)) return false;
  const autocomplete = control.autocomplete.toLocaleLowerCase("en");
  return control.type === "password"
    || control.type === "hidden"
    || autocomplete === "current-password"
    || autocomplete === "new-password"
    || autocomplete === "one-time-code";
}

function controlHasValue(control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean {
  if (control instanceof HTMLInputElement && (control.type === "checkbox" || control.type === "radio")) {
    return control.checked;
  }
  if (control instanceof HTMLInputElement && control.type === "file") {
    return Boolean(control.files?.length);
  }
  return control.value.length > 0;
}

function handleNetworkError(event: Event) {
  const detail = (event as CustomEvent<{
    requestId?: string;
    operation?: string;
    durationMs?: number;
  }>).detail;
  const duration = detail?.durationMs ?? 0;
  const durationBucket = duration < 250 ? "<250ms" : duration < 1_000 ? "250-1000ms" : duration < 3_000 ? "1-3s" : "3s+";
  trackTelemetry({
    eventType: "workflow_step",
    target: ANALYTICS_OPERATIONS.apiNetworkFailed,
    metadata: {
      workflowName: ANALYTICS_WORKFLOWS.apiRequest,
      workflowStep: "failed",
      outcome: "error",
      errorCategory: "network",
      errorCode: "NETWORK_ERROR",
      operation: detail?.operation?.slice(0, 60),
      requestId: detail?.requestId,
      durationBucket,
    },
  });
}

export default function TelemetryTracker() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = useAuthStore((state) => state.user?.id);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    return startPerformanceTracking(userId);
  }, [isAuthenticated, userId]);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    trackTelemetry({
      eventType: "page_view",
      pagePath: `${location.pathname}${location.search}`,
      metadata: {
        viewport: telemetryViewport(),
        language: document.documentElement.lang || navigator.language.slice(0, 10),
      },
    });
  }, [isAuthenticated, location.pathname, location.search, userId]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const interactive = event.target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
      if (!interactive || isIgnored(interactive)) return;

      trackTelemetry({
        eventType: "click",
        target: semanticElementLabel(interactive),
        metadata: { elementType: interactive.getAttribute("role") ?? interactive.tagName.toLocaleLowerCase("en") },
      });
    };

    const handleChange = (event: Event) => {
      const control = event.target;
      if (
        !(control instanceof HTMLInputElement)
        && !(control instanceof HTMLSelectElement)
        && !(control instanceof HTMLTextAreaElement)
      ) return;
      if (isIgnored(control) || isSensitiveControl(control)) return;

      const inputType = control instanceof HTMLInputElement
        ? control.type
        : control.tagName.toLocaleLowerCase("en");
      const includeLength = !(control instanceof HTMLInputElement)
        || !["checkbox", "radio", "file"].includes(control.type);

      trackTelemetry({
        eventType: "input_change",
        target: telemetryFieldName(control),
        metadata: {
          controlType: inputType,
          formName: formName(control.closest("form")),
          hasValue: controlHasValue(control),
          ...(includeLength ? { valueLengthBucket: telemetryValueLengthBucket(control.value.length) } : {}),
        },
      });
    };

    const handleSubmit = (event: SubmitEvent) => {
      if (!(event.target instanceof HTMLFormElement) || isIgnored(event.target)) return;
      trackTelemetry({
        eventType: "form_submit",
        target: formName(event.target),
        metadata: { elementType: "form" },
      });
      void flushTelemetry();
    };

    const handlePageHide = () => { void flushTelemetry(true); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") void flushTelemetry(true);
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("change", handleChange, true);
    document.addEventListener("submit", handleSubmit, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("uklid:api-network-error", handleNetworkError);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("change", handleChange, true);
      document.removeEventListener("submit", handleSubmit, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("uklid:api-network-error", handleNetworkError);
    };
  }, []);

  return null;
}
